import { EventSingleResponse, IEvent, IEventContext, isFunction, isPromiseLike } from '@ngt/opms';
import { useMemo } from 'react';
import { KeyedMutator, MutatorCallback } from 'swr';
import { GetConsentEventDataResponse } from '../../api/dtos';
import useConsentEventData from '../data/useConsentEventData';



const useGenerateEConsentEventContext = (consentEventContext: ReturnType<typeof useConsentEventData>) => {
    
    const eventContext: IEventContext = useMemo(() => {
        const event = consentEventContext.data?.event as IEvent;
        const error = consentEventContext.error;

        const eventMutate: KeyedMutator<EventSingleResponse> = async (data, opts) => {
            let newData: GetConsentEventDataResponse = { ...(consentEventContext.data as any), responseStatus: error as any };
            let newSingleData: EventSingleResponse | undefined = undefined;
            
            if (!data) {
                newSingleData = undefined;
            }
            else if (isFunction(data)) {
                const existingSingleData = { event, responseStatus: error } as EventSingleResponse;

                const callback = (data as MutatorCallback<EventSingleResponse>)(existingSingleData);
                
                if (isPromiseLike(callback)) {
                    newSingleData = await (callback as Promise<EventSingleResponse>);
                }
                else {
                    newSingleData = callback;
                }
            }
            else if (isPromiseLike(data)) {
                newSingleData = await (data as Promise<EventSingleResponse>);
            }

            if (!newSingleData) {
                newData.event = undefined as any;
            }
            else {
                if (newSingleData.responseStatus) {
                    newData.responseStatus = newSingleData.responseStatus;
                }

                newData.event = newSingleData.event;
            }

            const updatedData = await consentEventContext.mutate(newData, opts as any);

            if (updatedData) {
                return {
                    event: updatedData.event,
                    responseStatus: updatedData.responseStatus
                } as EventSingleResponse
            }

            return undefined;
        }

        const eventRefresh = async () => {
            const newData = await consentEventContext.refresh();

            return newData?.event;
        }

        return {
            data: event,
            error: consentEventContext.error,
            loading: consentEventContext.loading,
            validating: consentEventContext.validating,
            mutate: eventMutate,
            refresh: eventRefresh
        }
    }, [consentEventContext])

    return eventContext
}

export default useGenerateEConsentEventContext;